import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Section, SectionHeader } from "@/components/site/Section";
import { supabase } from "@/integrations/supabase/client";
import { amIReviewAdmin } from "@/utils/review.functions";
import { extractVideoId, formatRuntime, parseRuntime, type DemoTopic } from "@/lib/demo-topics";

export const Route = createFileRoute("/_authenticated/admin/demo")({
  head: () => ({
    meta: [
      { title: "Instant demo topics | Leadership Edge Live" },
      {
        name: "description",
        content: "Staff view for building and ordering the instant demo topics on How It Works.",
      },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Instant demo topics" },
      { property: "og:description", content: "Staff view for the instant demo topic list." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: AdminDemoPage,
});

const emptyDraft = {
  title: "",
  blurb: "",
  longUrl: "",
  longRuntime: "",
  shortUrl: "",
  shortRuntime: "",
  isOpening: false,
};

type Draft = typeof emptyDraft;

function toDraft(topic: DemoTopic): Draft {
  return {
    title: topic.title,
    blurb: topic.blurb ?? "",
    longUrl: topic.long_video_id ?? "",
    longRuntime: formatRuntime(topic.long_seconds),
    shortUrl: topic.short_video_id ?? "",
    shortRuntime: formatRuntime(topic.short_seconds),
    isOpening: topic.is_opening,
  };
}

const inputClass =
  "mt-1 w-full rounded-[6px] border border-border bg-background px-3 py-2 text-sm text-ink outline-none focus:border-primary";

function AdminDemoPage() {
  const checkAdmin = useServerFn(amIReviewAdmin);
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [topics, setTopics] = useState<DemoTopic[] | null>(null);
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const { data } = await supabase
      .from("demo_topics")
      .select(
        "id,title,blurb,long_video_id,long_seconds,short_video_id,short_seconds,sort_order,is_opening,published",
      )
      .order("is_opening", { ascending: false })
      .order("sort_order", { ascending: true });
    setTopics((data as DemoTopic[] | null) ?? []);
  }, []);

  useEffect(() => {
    checkAdmin()
      .then((result) => {
        setAllowed(result.admin);
        if (result.admin) void refresh();
      })
      .catch(() => setAllowed(false));
  }, [checkAdmin, refresh]);

  const save = async () => {
    setError(null);
    if (!draft.title.trim()) {
      setError("Give the topic a title.");
      return;
    }
    const longId = extractVideoId(draft.longUrl);
    const shortId = extractVideoId(draft.shortUrl);
    if (draft.longUrl.trim() && !longId) {
      setError("That long video link does not look like a Gumlet link.");
      return;
    }
    if (draft.shortUrl.trim() && !shortId) {
      setError("That short video link does not look like a Gumlet link.");
      return;
    }
    if (draft.isOpening && !longId) {
      setError("The opening video needs a Gumlet link.");
      return;
    }

    setBusy(true);
    const payload = {
      title: draft.title.trim(),
      blurb: draft.blurb.trim() || null,
      long_video_id: longId || null,
      long_seconds: parseRuntime(draft.longRuntime),
      short_video_id: shortId || null,
      short_seconds: parseRuntime(draft.shortRuntime),
      is_opening: draft.isOpening,
    };

    // Only one opening video at a time.
    if (draft.isOpening) {
      await supabase
        .from("demo_topics")
        .update({ is_opening: false })
        .eq("is_opening", true)
        .neq("id", editingId ?? "00000000-0000-0000-0000-000000000000");
    }

    const result = editingId
      ? await supabase.from("demo_topics").update(payload).eq("id", editingId)
      : await supabase
          .from("demo_topics")
          .insert({ ...payload, sort_order: (topics?.length ?? 0) + 1 });

    setBusy(false);
    if (result.error) {
      setError(result.error.message);
      return;
    }
    setDraft(emptyDraft);
    setEditingId(null);
    void refresh();
  };

  const move = async (topic: DemoTopic, direction: -1 | 1) => {
    const list = (topics ?? []).filter((t) => !t.is_opening);
    const index = list.findIndex((t) => t.id === topic.id);
    const swapWith = list[index + direction];
    if (!swapWith) return;
    setBusy(true);
    await supabase
      .from("demo_topics")
      .update({ sort_order: swapWith.sort_order })
      .eq("id", topic.id);
    await supabase
      .from("demo_topics")
      .update({ sort_order: topic.sort_order })
      .eq("id", swapWith.id);
    setBusy(false);
    void refresh();
  };

  const togglePublished = async (topic: DemoTopic) => {
    setBusy(true);
    await supabase.from("demo_topics").update({ published: !topic.published }).eq("id", topic.id);
    setBusy(false);
    void refresh();
  };

  const remove = async (topic: DemoTopic) => {
    setBusy(true);
    await supabase.from("demo_topics").delete().eq("id", topic.id);
    setBusy(false);
    if (editingId === topic.id) {
      setEditingId(null);
      setDraft(emptyDraft);
    }
    void refresh();
  };

  if (allowed === null) {
    return (
      <Section tone="default">
        <p className="text-sm text-muted-foreground">Checking your access...</p>
      </Section>
    );
  }

  if (!allowed) {
    return (
      <Section tone="default">
        <SectionHeader
          title="Staff only"
          intro="This page is for Leadership Edge Live staff. Ask us to add you if you need access."
        />
      </Section>
    );
  }

  const ordered = topics ?? [];

  return (
    <Section tone="default" className="max-w-4xl">
      <SectionHeader
        eyebrow="Instant demo"
        title="Demo topics"
        intro="Add a topic, paste the long and short Gumlet links, set the runtimes, then order the list. Visitors on How It Works pick what matters and watch straight away."
        titleClassName="text-3xl font-semibold leading-tight text-ink md:text-4xl"
        titleStyle={{ fontFamily: "'Poppins', sans-serif" }}
      />

      <div
        className="rounded-2xl border border-border/60 bg-background p-6"
        style={{ fontFamily: "'Poppins', sans-serif" }}
      >
        <h3 className="text-base font-semibold text-ink">
          {editingId ? "Edit topic" : "Add a topic"}
        </h3>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <label className="block text-sm md:col-span-2">
            <span className="font-medium text-ink">Title</span>
            <input
              className={inputClass}
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              placeholder="Building accountability"
            />
          </label>
          <label className="block text-sm md:col-span-2">
            <span className="font-medium text-ink">One line description (optional)</span>
            <input
              className={inputClass}
              value={draft.blurb}
              onChange={(e) => setDraft({ ...draft, blurb: e.target.value })}
              placeholder="What this topic covers, in a sentence."
            />
          </label>
          <label className="block text-sm">
            <span className="font-medium text-ink">Long video link</span>
            <input
              className={inputClass}
              value={draft.longUrl}
              onChange={(e) => setDraft({ ...draft, longUrl: e.target.value })}
              placeholder="https://play.gumlet.io/embed/..."
            />
          </label>
          <label className="block text-sm">
            <span className="font-medium text-ink">Long runtime</span>
            <input
              className={inputClass}
              value={draft.longRuntime}
              onChange={(e) => setDraft({ ...draft, longRuntime: e.target.value })}
              placeholder="4:30"
            />
          </label>
          <label className="block text-sm">
            <span className="font-medium text-ink">Short video link</span>
            <input
              className={inputClass}
              value={draft.shortUrl}
              onChange={(e) => setDraft({ ...draft, shortUrl: e.target.value })}
              placeholder="https://play.gumlet.io/embed/..."
            />
          </label>
          <label className="block text-sm">
            <span className="font-medium text-ink">Short runtime</span>
            <input
              className={inputClass}
              value={draft.shortRuntime}
              onChange={(e) => setDraft({ ...draft, shortRuntime: e.target.value })}
              placeholder="1:15"
            />
          </label>
          <label className="flex items-center gap-2 text-sm md:col-span-2">
            <input
              type="checkbox"
              checked={draft.isOpening}
              onChange={(e) => setDraft({ ...draft, isOpening: e.target.checked })}
            />
            <span className="text-ink">
              This is the required opening video everyone watches first
            </span>
          </label>
        </div>

        {error && <p className="mt-4 text-sm font-medium text-destructive">{error}</p>}

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <button
            type="button"
            disabled={busy}
            onClick={() => void save()}
            className="inline-flex items-center rounded-[6px] bg-[#693D94] px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-brand transition-transform hover:-translate-y-0.5 disabled:opacity-60"
          >
            {editingId ? "Save changes" : "Add topic"}
          </button>
          {editingId && (
            <button
              type="button"
              onClick={() => {
                setEditingId(null);
                setDraft(emptyDraft);
              }}
              className="text-sm font-semibold text-primary underline-offset-4 hover:underline"
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      <div className="mt-8" style={{ fontFamily: "'Poppins', sans-serif" }}>
        <h3 className="text-base font-semibold text-ink">
          Topic order ({ordered.length} {ordered.length === 1 ? "topic" : "topics"})
        </h3>
        <ul className="mt-3 divide-y divide-border/60 border-y border-border/60">
          {ordered.map((topic) => (
            <li key={topic.id} className="flex flex-col gap-3 py-4 md:flex-row md:items-start">
              <div className="flex-1">
                <p className="text-sm font-semibold text-ink">
                  {topic.title}
                  {topic.is_opening && (
                    <span className="ml-2 rounded-full bg-lavender px-2 py-0.5 text-xs font-semibold text-primary">
                      Opening
                    </span>
                  )}
                  {!topic.published && (
                    <span className="ml-2 text-xs font-medium text-muted-foreground">Hidden</span>
                  )}
                </p>
                {topic.blurb && (
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                    {topic.blurb}
                  </p>
                )}
                <p className="mt-1 text-xs text-muted-foreground">
                  Long: {topic.long_video_id ?? "none"}{" "}
                  {topic.long_seconds ? `(${formatRuntime(topic.long_seconds)})` : ""} · Short:{" "}
                  {topic.short_video_id ?? "none"}{" "}
                  {topic.short_seconds ? `(${formatRuntime(topic.short_seconds)})` : ""}
                </p>
              </div>
              <div className="flex shrink-0 flex-wrap items-center gap-3 text-sm font-semibold text-primary">
                {!topic.is_opening && (
                  <>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void move(topic, -1)}
                      className="hover:underline"
                    >
                      Up
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void move(topic, 1)}
                      className="hover:underline"
                    >
                      Down
                    </button>
                  </>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setEditingId(topic.id);
                    setDraft(toDraft(topic));
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  className="hover:underline"
                >
                  Edit
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void togglePublished(topic)}
                  className="hover:underline"
                >
                  {topic.published ? "Hide" : "Show"}
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void remove(topic)}
                  className="text-destructive hover:underline"
                >
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
        {ordered.length === 0 && (
          <p className="mt-3 text-sm text-muted-foreground">
            No topics yet. Start with the opening video.
          </p>
        )}
      </div>
    </Section>
  );
}
